import type { FC } from 'react'
import React, { useCallback, useEffect, useMemo, useState } from 'react'

import type { DialogueModalProps } from './types'
import useExtractProps from './use-extract-props'
import { useUniqueId } from '../../utils/unique-id'
import ModalPane from '../_layouts/pane/modal-pane'
import type { ModalPaneProps } from '../_layouts/pane/types'
import Flex from '../_primitives/flex/flex'
import Button from '../button/button'
import Card from '../card/card'
import Checkbox from '../checkbox/checkbox'

const defaultModalProps: Omit<ModalPaneProps, 'children'> = {
  useModalAsBackdrop: true
}

/**
 * Designed intentionally to use confirmAction to be dynamically apply.
 * This allow the consumer to provide a closure callback that may have varying state
 * at runtime.
 */
const DialogueModal: FC<DialogueModalProps> = props => {
  const {
    variant,
    display,
    flexDirection,
    modalProps,
    headerActions,
    footer,
    _open,
    confirmAction,
    cancelAction,
    checkboxProps,
    checkboxPlacement,
    rest,
    cancelLabel,
    okLabel,
    titleLabel,
    descriptionsText,
    contentProps,
    hideCloseButton,
    restCloseButtonProps,
    hideCancelButton,
    restCancelButtonProps,
    hideConfirmButton,
    restConfirmButtonProps,
    autoCloseOnConfirm
  } = useExtractProps(props)

  const [open, setOpen] = useState(!!_open)

  useEffect(() => {
    if (!confirmAction) {
      setOpen(!!_open)
    }
  }, [_open, confirmAction])

  useEffect(() => {
    setOpen(!!confirmAction)
  }, [confirmAction])

  const handleConfirmOk = useCallback(() => {
    confirmAction && confirmAction()

    if (autoCloseOnConfirm) {
      setOpen(false)
    }
  }, [confirmAction, autoCloseOnConfirm])

  const handleCancelClick = useCallback(() => {
    cancelAction && cancelAction()
    setOpen(false)
  }, [cancelAction])

  const titleId = useUniqueId('csg-tui-confirmation-title')
  const descriptionId = useUniqueId('csg-tui-confirmation-description')

  const buttonOptions = useMemo(
    () =>
      hideCancelButton && hideConfirmButton ? undefined : (
        <Flex gap="xs">
          {!hideCancelButton && cancelAction && (
            <Button
              variant="secondary"
              minWidth="5rem"
              onClick={handleCancelClick}
              {...restCancelButtonProps}
            >
              {cancelLabel}
            </Button>
          )}
          {!hideConfirmButton && (
            <Button
              variant="primary"
              minWidth="5rem"
              onClick={handleConfirmOk}
              {...restConfirmButtonProps}
            >
              {okLabel}
            </Button>
          )}
        </Flex>
      ),
    [
      cancelAction,
      cancelLabel,
      handleCancelClick,
      handleConfirmOk,
      hideCancelButton,
      hideConfirmButton,
      okLabel,
      restCancelButtonProps,
      restConfirmButtonProps
    ]
  )
  const _footer = useMemo(
    () =>
      footer ? (
        footer
      ) : buttonOptions ? (
        checkboxPlacement === 'above' || checkboxPlacement === 'below' ? (
          <Flex flexDirection="column" gap="sm">
            {checkboxPlacement === 'above' && checkboxProps && (
              <Checkbox {...checkboxProps} />
            )}
            {buttonOptions}
            {checkboxPlacement === 'below' && checkboxProps && (
              <Checkbox {...checkboxProps} />
            )}
          </Flex>
        ) : (
          <>
            {checkboxPlacement === 'before' && checkboxProps && (
              <Checkbox {...checkboxProps} />
            )}
            {buttonOptions}
            {checkboxPlacement === 'after' && checkboxProps && (
              <Checkbox {...checkboxProps} />
            )}
          </>
        )
      ) : undefined,
    [buttonOptions, checkboxPlacement, checkboxProps, footer]
  )

  const modalContent = useMemo(
    () => (
      <Card
        variant={variant}
        title={titleLabel}
        display={display}
        flexDirection={flexDirection}
        headerActions={
          cancelAction ? (
            <>
              {headerActions && headerActions}
              {!hideCloseButton && (
                <Button
                  variant="icon"
                  icon="close"
                  iconSize="xl"
                  color="neutral.dark"
                  p="0"
                  onClick={handleCancelClick}
                  {...restCloseButtonProps}
                />
              )}
            </>
          ) : (
            headerActions
          )
        }
        contentProps={contentProps}
        footer={_footer}
        {...rest}
      >
        {descriptionsText}
      </Card>
    ),
    [
      _footer,
      cancelAction,
      contentProps,
      descriptionsText,
      display,
      flexDirection,
      handleCancelClick,
      headerActions,
      hideCloseButton,
      rest,
      restCloseButtonProps,
      titleLabel,
      variant
    ]
  )

  return (
    <ModalPane
      open={open}
      variant="modal.cosmos"
      aria-labelledby={titleId}
      aria-describedby={descriptionId}
      {...{ ...defaultModalProps, ...modalProps }}
    >
      {modalContent}
    </ModalPane>
  )
}

export default DialogueModal
